import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, CheckCircle2, AlertCircle, BookOpen, Check, Printer, Loader2, Eye, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import StudentPdfPreviewModal from "./StudentPdfPreviewModal";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchEvaluationReport, updateAnswerMarks } from "./EvaluatorSlice";
import { ApiEvaluationAnswer } from "./types";

interface EvaluationReportProps {
    sheetId: string;
    onBack: () => void;
}

const formatMarks = (n: number | null | undefined): string => {
    if (n === null || n === undefined || isNaN(Number(n))) return "0";
    return String(parseFloat(Number(n).toFixed(1)));
};

const PrintButton = () => {
    const handlePrint = () => {
        window.print();
    };

    return (
        <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 p-2 sm:px-4 sm:py-2 bg-gray-900 text-white rounded-lg text-xs font-bold uppercase tracking-wide hover:bg-black transition-all print:hidden"
            title="Print / Save PDF"
        >
            <Printer className="w-4 h-4" />
            <span className="hidden sm:inline">Print / Save PDF</span>
        </button>
    );
};

const EvaluationReport: React.FC<EvaluationReportProps> = ({ sheetId, onBack }) => {
    const dispatch = useAppDispatch();
    const { currentReport, reportLoading, reportError } = useAppSelector((state: any) => state.evaluator);

    const [activeQuestionId, setActiveQuestionId] = useState<string>("");
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [showQuestionPdf, setShowQuestionPdf] = useState(false);
    const [savingAnswerId, setSavingAnswerId] = useState<string | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const handleMarksUpdate = async (answerId: string, marks: number): Promise<boolean> => {
        if (!currentReport?.evaluation_id) {
            toast.error("Evaluation ID missing — reload the report and try again");
            return false;
        }
        setSavingAnswerId(answerId);
        try {
            await dispatch(
                updateAnswerMarks({
                    evaluationId: currentReport.evaluation_id,
                    answerId,
                    marks,
                })
            ).unwrap();
            toast.success("Marks updated");
            return true;
        } catch (err: any) {
            toast.error(typeof err === "string" ? err : "Failed to update marks");
            return false;
        } finally {
            setSavingAnswerId(null);
        }
    };

    useEffect(() => {
        if (sheetId) {
            dispatch(fetchEvaluationReport(sheetId));
        }
    }, [dispatch, sheetId]);

    useEffect(() => {
        // Observer for active section (disabled during print via CSS generally, but JS still runs)
        const options = {
            root: document.getElementById('report-scroll-container'),
            rootMargin: '-20% 0px -50% 0px',
            threshold: 0.1
        };

        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveQuestionId(entry.target.id);
                }
            });
        };

        const container = document.getElementById('report-scroll-container');
        if (container) {
            observerRef.current = new IntersectionObserver(callback, options);
            const sections = document.querySelectorAll('.question-section');
            sections.forEach((section) => {
                observerRef.current?.observe(section);
            });
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [currentReport]);

    useEffect(() => {
        if (activeQuestionId) {
            const index = activeQuestionId.split('-')[1];
            const sidebarElement = document.getElementById(`sidebar-nav-${index}`);
            if (sidebarElement) {
                sidebarElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [activeQuestionId]);

    const scrollToQuestion = (index: number) => {
        const element = document.getElementById(`question-${index}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => setActiveQuestionId(`question-${index}`), 500);
        }
    };

    if (reportLoading) {
        return (
            <div className="flex-1 flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (reportError) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center h-full gap-4">
                <div className="text-red-500 font-bold">Failed to load report</div>
                <button onClick={onBack} className="text-primary hover:underline">Go Back</button>
            </div>
        );
    }

    if (!currentReport) return null;

    return (
        <div className="flex-1 bg-gray-50/30 flex flex-col h-full overflow-hidden print:overflow-visible print:bg-white print:h-auto">
            {/* Global Print Styles */}
            <style>{`
                @media print {
                    @page {
                        margin: 20mm;
                        size: A4;
                    }
                    body {
                        visibility: hidden;
                        background-color: white;
                    }
                    /* Hide parent containers' clipping */
                    html, body, #root {
                        height: auto !important;
                        overflow: visible !important;
                    }
                    /* Make the report container visible and absolute to cover everything */
                    #report-printable-area {
                        visibility: visible;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        margin: 0;
                        padding: 0;
                        background-color: white;
                    }
                    /* Ensure children are visible */
                    #report-printable-area * {
                        visibility: visible;
                    }
                    
                    /* Hide scrollbars and specific UI elements explicitly */
                    .no-print, .print\\:hidden {
                        display: none !important;
                    }

                    /* Better page breaks */
                    .question-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        margin-bottom: 15px;
                    }
                    /* Hide box shadows to save ink/render better */
                    .shadow-xl, .shadow-sm {
                        box-shadow: none !important;
                    }
                }
            `}</style>

            {/* Header / Nav - Hidden in Print */}
            <div className="px-4 py-4 md:px-6 md:py-4 bg-white border-b border-border flex flex-col sm:flex-row items-center justify-between sticky top-0 z-10 gap-4 shadow-sm print:hidden">
                <div className="flex items-center gap-3 w-full sm:w-auto overflow-hidden">
                    <button
                        onClick={onBack}
                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-900 shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="overflow-hidden">
                        <h2 className="text-base md:text-lg font-bold text-gray-900 leading-tight truncate">Evaluation Report</h2>
                        <span className="text-[10px] text-gray-500 font-mono block truncate">ID: {sheetId}</span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto justify-end">
                    {/* Question PDF Button */}
                    {currentReport.question_paper_url && (
                        <button
                            onClick={() => setShowQuestionPdf(true)}
                            className="flex items-center justify-center gap-1 sm:gap-1.5 px-2 py-1.5 sm:px-3 sm:py-2 bg-black text-white rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wide hover:bg-gray-800 transition-all shrink-0"
                            title="Question PDF"
                        >
                            <BookOpen className="w-4 h-4 shrink-0" />
                            <span className="hidden sm:inline">Question PDF</span>
                            <span className="sm:hidden">Q. PDF</span>
                        </button>
                    )}

                    {/* PDF Preview Button */}
                    {currentReport.student_answer_url && (
                        <button
                            onClick={() => setShowPdfPreview(true)}
                            className="flex items-center justify-center gap-1 sm:gap-1.5 px-2 py-1.5 sm:px-3 sm:py-2 bg-white border border-gray-200 text-black rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wide hover:bg-gray-50 transition-all shrink-0"
                            title="PDF Preview"
                        >
                            <Eye className="w-4 h-4 shrink-0" />
                            <span className="hidden sm:inline">Answer PDF</span>
                            <span className="sm:hidden">A. PDF</span>
                        </button>
                    )}

                    {/* Native Print Button */}
                    <div className="flex shrink-0">
                        <PrintButton />
                    </div>

                    <div className="flex items-center gap-1.5 px-2 sm:px-3 h-8 sm:h-9 bg-green-50 text-green-700 rounded-full border border-green-100 shrink-0" title={currentReport.status || 'Completed'}>
                        <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="hidden md:inline text-[10px] sm:text-xs font-bold uppercase tracking-wide">{currentReport.status || 'Completed'}</span>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden print:overflow-visible print:h-auto">
                {/* Scrollable Report Area */}
                <div
                    id="report-scroll-container"
                    className="flex-1 overflow-y-auto md:p-6 scrollbar-hide scroll-smooth print:overflow-visible print:h-auto print:p-0"
                >
                    {/* Printable Wrapper */}
                    <div id="report-printable-area" className="max-w-4xl mx-auto space-y-8 pb-10 bg-gray-50/30 p-4 relative print:bg-white print:p-0 print:max-w-none">

                        {/* Score Card */}
                        <div className="bg-black rounded-2xl p-5 md:p-6 text-white shadow-xl shadow-black/20 relative overflow-hidden print:bg-none print:bg-white print:text-black print:border print:border-black print:shadow-none">
                            <div className="absolute top-0 right-0 p-32 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none print:hidden" />

                            <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 items-center gap-6 sm:gap-4">
                                <div className="col-span-1">
                                    <p className="text-white/80 text-[10px] sm:text-xs font-bold mb-1 uppercase tracking-wider print:text-gray-900">Overall Score</p>
                                    <div className="text-2xl sm:text-4xl font-black tracking-tight">
                                        {formatMarks(currentReport.overall_score)} <span className="text-lg sm:text-xl font-bold opacity-90">/ {currentReport.max_score}</span>
                                    </div>
                                </div>

                                <div className="col-span-2 sm:col-span-1 order-3 sm:order-2 text-center sm:text-center pt-4 sm:pt-0 border-t sm:border-t-0 border-white/10">
                                    <p className="text-white/80 text-[10px] sm:text-xs uppercase tracking-widest mb-1 print:text-gray-900">Student Identity</p>
                                    <p className="text-lg sm:text-xl font-bold leading-tight">{currentReport.student_name || "Unknown"}</p>
                                    {currentReport.student_roll_number && (
                                        <p className="text-white/80 text-xs mt-1 print:text-gray-900">
                                            Roll No: <span className="text-white font-bold print:text-black">{currentReport.student_roll_number}</span>
                                        </p>
                                    )}
                                </div>

                                <div className="col-span-1 order-2 sm:order-3 text-right">
                                    <p className="text-white/80 text-[10px] sm:text-xs font-bold mb-1 uppercase tracking-wider print:text-gray-900">Percentage</p>
                                    <div className="text-2xl sm:text-3xl font-bold">{currentReport.percentage ? `${currentReport.percentage.toFixed(1)}%` : '0%'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Section Title */}
                        <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
                            <BookOpen className="w-5 h-5 text-gray-400 print:text-black" />
                            <h3 className="text-lg font-bold text-gray-900">Question-wise Evaluation</h3>
                        </div>

                        {/* Questions List */}
                        <div className="space-y-6 print:space-y-4">
                            {currentReport.answers && currentReport.answers.map((item: ApiEvaluationAnswer, index: number) => (
                                <div key={item.answer_id} id={`question-${index}`} className="question-section scroll-mt-24">
                                    <QuestionCard
                                        data={item}
                                        index={index}
                                        isSaving={savingAnswerId === item.answer_id}
                                        onMarksUpdate={(marks) => handleMarksUpdate(item.answer_id, marks)}
                                    />
                                </div>
                            ))}
                        </div>

                    </div>
                </div>

                {/* Right Sidebar - Sticky Navigation (Hidden in Print) */}
                <div className="w-[320px] bg-transparent hidden lg:flex flex-col h-full overflow-hidden print:hidden">
                    <div className="p-6 h-full overflow-y-auto">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Contents</h3>
                        </div>

                        <div className="relative pl-2">
                            {/* Continuous Line */}
                            <div className="absolute top-4 bottom-4 left-[15px] w-0.5 bg-gray-100 rounded-full" />

                            <div className="space-y-4 relative">
                                {currentReport.answers && currentReport.answers.map((item: ApiEvaluationAnswer, index: number) => {
                                    const isActive = activeQuestionId === `question-${index}`;

                                    return (
                                        <button
                                            key={index}
                                            id={`sidebar-nav-${index}`}
                                            onClick={() => scrollToQuestion(index)}
                                            className="w-full text-left group relative pl-8"
                                        >
                                            {/* Timeline Dot */}
                                            <div className={`absolute left-[9px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 transition-all duration-300 z-10 ${isActive
                                                ? "bg-black border-black scale-110"
                                                : "bg-white border-gray-300 group-hover:border-gray-400"
                                                }`} />

                                            {/* Content Card */}
                                            <div className={`p-4 rounded-xl transition-all duration-300 border ${isActive
                                                ? "bg-white shadow-lg shadow-black/5 border-gray-100 translate-x-1"
                                                : "bg-transparent border-transparent hover:bg-gray-50 text-gray-400 grayscale"
                                                }`}>
                                                <div className="flex justify-between items-start gap-4">
                                                    <div>
                                                        <p className={`text-sm font-bold mb-1 transition-colors ${isActive ? "text-gray-900" : "text-gray-500"
                                                            }`}>
                                                            Question {item.question_number || index + 1}
                                                        </p>
                                                    </div>

                                                    <div className={`px-2.5 py-1 rounded-md text-[10px] font-bold border transition-colors ${isActive
                                                        ? "bg-gray-900 text-white border-gray-900"
                                                        : "bg-gray-100 text-gray-500 border-transparent"
                                                        }`}>
                                                        {formatMarks(item.marks_obtained)}/{item.max_marks}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* PDF Preview Modal */}
            {currentReport.student_answer_url && (
                <StudentPdfPreviewModal
                    isOpen={showPdfPreview}
                    onClose={() => setShowPdfPreview(false)}
                    pdfUrl={currentReport.student_answer_url}
                    studentName={currentReport.student_name}
                />
            )}

            {/* Question Paper PDF Modal */}
            {currentReport.question_paper_url && (
                <StudentPdfPreviewModal
                    isOpen={showQuestionPdf}
                    onClose={() => setShowQuestionPdf(false)}
                    pdfUrl={currentReport.question_paper_url}
                    studentName="Question Paper"
                />
            )}
        </div>
    );
};

interface QuestionCardProps {
    data: ApiEvaluationAnswer;
    index: number;
    isSaving: boolean;
    onMarksUpdate: (marks: number) => Promise<boolean>;
}

const QuestionCard = ({ data, index, isSaving, onMarksUpdate }: QuestionCardProps) => {
    const obtainedMarks = data.marks_obtained;
    const displayMarks = formatMarks(obtainedMarks);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState<string>(displayMarks);

    const beginEdit = () => {
        setEditValue(displayMarks);
        setIsEditing(true);
    };

    const handleSave = async () => {
        const parsed = parseFloat(editValue);
        if (isNaN(parsed) || parsed < 0 || parsed > data.max_marks) {
            toast.error(`Marks must be between 0 and ${data.max_marks}`);
            return;
        }
        const ok = await onMarksUpdate(parsed);
        if (ok) setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(displayMarks);
        setIsEditing(false);
    };

    return (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden transition-all hover:shadow-md print:shadow-none print:border-gray-300">
            {/* Question Header */}
            <div className="px-4 py-2 md:px-6 md:py-3 border-b border-gray-50 bg-gray-50/30 flex flex-col md:flex-row justify-between items-start gap-3 print:bg-white print:border-gray-200">
                <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-0.5">Question {data.question_number || index + 1}</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">{data.question_text}</p>
                </div>
                <div className="shrink-0 flex items-center md:items-end gap-2 w-full md:w-auto justify-center md:justify-end">
                    <span className="md:hidden text-xs text-gray-500 font-semibold uppercase tracking-wider print:hidden">Marks</span>
                    {isEditing ? (
                        <div className="flex items-end gap-1.5 print:hidden">
                            <div className="flex flex-col items-center">
                                <span className="hidden md:inline text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1 print:hidden">Marks</span>
                                <div className="flex items-center h-9 bg-gray-50 border border-gray-200 rounded-md px-2.5 focus-within:ring-2 focus-within:ring-black/10 focus-within:border-black transition">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={editValue}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "" || /^\d*\.?\d{0,1}$/.test(v)) setEditValue(v);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleSave();
                                            if (e.key === "Escape") handleCancel();
                                        }}
                                        onFocus={(e) => e.target.select()}
                                        autoFocus
                                        disabled={isSaving}
                                        className="w-9 text-lg font-bold leading-none bg-transparent text-gray-900 text-center focus:outline-none disabled:opacity-60"
                                    />
                                    <span className="text-xs text-gray-500 whitespace-nowrap ml-1">/ {data.max_marks}</span>
                                </div>
                            </div>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="inline-flex items-center justify-center h-9 w-9 bg-green-500 text-white rounded-md hover:bg-green-600 active:scale-95 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                title="Save"
                                aria-label="Save marks"
                            >
                                {isSaving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Check className="w-4 h-4" strokeWidth={3} />
                                )}
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="inline-flex items-center justify-center h-9 w-9 bg-gray-100 text-gray-500 rounded-md hover:bg-gray-200 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                title="Cancel"
                                aria-label="Cancel edit"
                            >
                                <X className="w-4 h-4" strokeWidth={3} />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-end gap-1.5 group/marks">
                            <div className="flex flex-col items-center">
                                <span className="hidden md:inline text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1 print:hidden">Marks</span>
                                <span className="inline-flex items-baseline h-9 px-3 rounded-md border border-gray-200 text-lg font-bold leading-none bg-white text-gray-900 print:bg-white print:text-black print:border-black">
                                    <span className="self-center">{displayMarks}</span>
                                    <span className="text-xs opacity-60 ml-1 self-center">/ {data.max_marks}</span>
                                </span>
                            </div>
                            <button
                                onClick={beginEdit}
                                className="inline-flex items-center justify-center h-9 w-9 text-black bg-white border border-gray-200 hover:bg-gray-50 rounded-md transition-colors print:hidden"
                                aria-label="Edit marks"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-4 py-2 md:px-6 md:py-3 space-y-3">
                {/* Student Answer */}
                {data.student_answer && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 print:bg-white print:border-gray-200 print:text-black">
                        <div className="flex items-center gap-2 mb-2 text-gray-900 print:text-black">
                            <Eye className="w-4 h-4" />
                            <span className="text-xs font-bold uppercase tracking-wide">Student's Answer</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-medium">
                            {data.student_answer}
                        </p>
                    </div>
                )}

                {/* Feedback Grid */}
                <div className="grid md:grid-cols-1 gap-4">
                    {/* Strengths */}
                    {data.strengths.length > 0 && (
                        <div className="bg-green-50/50 rounded-lg p-4 border border-green-100 print:bg-white print:border-gray-200 print:text-black">
                            <div className="flex items-center gap-2 mb-3 text-green-700 print:text-black">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-xs font-bold uppercase tracking-wide">Strengths</span>
                            </div>
                            <ul className="space-y-2">
                                {data.strengths.map((point, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-green-800 print:text-black">
                                        <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0 print:text-black" />
                                        <span>{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Improvements */}
                    {data.improvements.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 print:bg-white print:border-gray-200 print:text-black">
                            <div className="flex items-center gap-2 mb-3 text-gray-900 print:text-black">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-xs font-bold uppercase tracking-wide">Areas for Improvement</span>
                            </div>
                            <ul className="space-y-2">
                                {data.improvements.map((point, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 print:text-black">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0 print:bg-black" />
                                        <span>{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EvaluationReport;
